import type {
  FreeLayoutPluginContext,
  WorkflowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
import { nanoid } from 'nanoid';
import type { FlowNodeData, ModelConfig } from '$/domains/workFlowV3/type';
import { NODE_NAME_CONFIG, TVFlowNodeNextTypeEnum } from '../config/node.config';
import { getNextNodeNameByCurrentType } from './nodeNameCounter';
import {
  resolveQuickStartApplyConfig,
  type QuickStartType,
} from './quickStart';
import { appendMediaPreviewOrderId } from '../FlowContent/components/NodeBottomPanel/inputNodePreview';

export type LeftLinkedNodePoint = {
  x: number;
  y: number;
};

type InputNodeIdField = 'textNodeIds' | 'imageNodeIds' | 'videoNodeIds' | 'audioNodeIds';

type LinkedNodeData = Record<string, any> & {
  inputNodeId?: FlowNodeData['inputNodeId'];
};

/** 左侧输入节点相对当前节点的默认位置。 */
const DEFAULT_LEFT_NODE_OFFSET: LeftLinkedNodePoint = {
  x: -360,
  y: 0,
};

/** 批量创建多个左侧节点时，节点之间的默认垂直间距。 */
const DEFAULT_LEFT_NODE_VERTICAL_GAP = 280;

/** 左侧节点连入当前节点后，需要同步到当前节点 data.inputNodeId 的字段。 */
const NODE_TYPE_TO_INPUT_NODE_ID_FIELD: Partial<Record<TVFlowNodeNextTypeEnum, InputNodeIdField>> = {
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: 'textNodeIds',
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: 'imageNodeIds',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: 'videoNodeIds',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: 'audioNodeIds',
};

/**
 * 要创建在当前节点左侧并连入当前节点的节点配置。
 */
export interface LeftLinkedNodeConfig<TData extends Record<string, unknown> = Record<string, unknown>> {
  /** 可选。未传时内部自动生成 nanoid。 */
  id?: string;
  /** 要创建的节点类型。 */
  type: TVFlowNodeNextTypeEnum;
  /** 新节点 data，调用方按目标节点类型自行组装。 */
  data: TData;
  /**
   * 可选。相对当前节点位置的偏移量。
   * 未传时使用 defaultOffset，并按 verticalGap 自动错开多个左侧节点。
   */
  offset?: LeftLinkedNodePoint;
}

/**
 * 当前节点 data 的更新方式。
 * - 传对象：直接浅合并到当前节点 data。
 * - 传函数：可基于最新 currentData 和已创建的 leftNodes 计算 patch。
 */
export type CurrentDataPatch<TCurrentData extends LinkedNodeData> =
  | Partial<TCurrentData>
  | ((params: {
    currentData: TCurrentData;
    leftNodes: WorkflowNodeEntity[];
  }) => Partial<TCurrentData>);

interface ApplyLeftLinkedNodesBaseParams<TCurrentData extends LinkedNodeData> {
  /** 当前被操作的节点，即左侧节点最终连入的目标节点。 */
  currentNode: WorkflowNodeEntity;
  /** 可选。创建并连线后，对 currentNode.data 做一次浅合并更新。 */
  currentDataPatch?: CurrentDataPatch<TCurrentData>;
  /** 可选。所有左侧节点的默认相对偏移，默认在当前节点左侧 360px。 */
  defaultOffset?: LeftLinkedNodePoint;
  /** 可选。多个左侧节点自动纵向错开时的间距。 */
  verticalGap?: number;
}

export interface ApplyManualLeftLinkedNodesParams<TCurrentData extends LinkedNodeData>
  extends ApplyLeftLinkedNodesBaseParams<TCurrentData> {
  /** 需要批量创建在 currentNode 左侧的输入节点。 */
  leftNodes: LeftLinkedNodeConfig[];
}

export interface ApplyQuickStartLeftLinkedNodesParams<TCurrentData extends LinkedNodeData>
  extends ApplyLeftLinkedNodesBaseParams<TCurrentData> {
  /** 快速开始 action key，工具内部按配置生成左侧节点与当前节点 patch。 */
  quickStartType: QuickStartType | string;
  /** 可选。未传时内部从 TeamFlow store 获取。 */
  modelConfig?: ModelConfig;
}

export type ApplyLeftLinkedNodesParams<TCurrentData extends LinkedNodeData> =
  | ApplyManualLeftLinkedNodesParams<TCurrentData>
  | ApplyQuickStartLeftLinkedNodesParams<TCurrentData>;

export interface ApplyLeftLinkedNodesResult<TCurrentData extends LinkedNodeData> {
  /** 实际创建出来的左侧节点实体。 */
  leftNodes: WorkflowNodeEntity[];
  /** 合并 currentDataPatch 和 inputNodeId 后的当前节点 data。 */
  currentData: TCurrentData;
}

function dedupeNodeIds(ids: string[]) {
  return [...new Set(ids)];
}

function createInputNodeId(existing?: FlowNodeData['inputNodeId']): NonNullable<FlowNodeData['inputNodeId']> {
  const nextInputNodeId: NonNullable<FlowNodeData['inputNodeId']> = {
    textNodeIds: dedupeNodeIds(existing?.textNodeIds ?? []),
    imageNodeIds: dedupeNodeIds(existing?.imageNodeIds ?? []),
    videoNodeIds: dedupeNodeIds(existing?.videoNodeIds ?? []),
    audioNodeIds: dedupeNodeIds(existing?.audioNodeIds ?? []),
  };

  if (existing?.mediaPreviewOrder?.length) {
    nextInputNodeId.mediaPreviewOrder = [...existing.mediaPreviewOrder];
  }

  return nextInputNodeId;
}

function mergeLeftNodeIdsToInputNodeId(
  inputNodeId: FlowNodeData['inputNodeId'],
  leftNodes: WorkflowNodeEntity[],
) {
  const nextInputNodeId = createInputNodeId(inputNodeId);
  let hasMergedNode = false;

  leftNodes.forEach((leftNode) => {
    const field = NODE_TYPE_TO_INPUT_NODE_ID_FIELD[leftNode.flowNodeType as TVFlowNodeNextTypeEnum];
    if (!field) return;

    hasMergedNode = true;
    nextInputNodeId[field] = dedupeNodeIds([
      ...nextInputNodeId[field],
      leftNode.id,
    ]);

    if (field === 'imageNodeIds' || field === 'videoNodeIds') {
      const orderedInputNodeId = appendMediaPreviewOrderId(nextInputNodeId, leftNode.id, field);
      nextInputNodeId.mediaPreviewOrder = orderedInputNodeId.mediaPreviewOrder;
    }
  });

  return hasMergedNode ? nextInputNodeId : inputNodeId;
}

function getCurrentNodePosition(
  ctx: FreeLayoutPluginContext,
  currentNode: WorkflowNodeEntity,
) {
  const currentNodeJSON = currentNode.toJSON();
  return (currentNodeJSON.meta as { position?: LeftLinkedNodePoint; } | undefined)?.position
    ?? ctx.document.getNodeDefaultPosition(currentNode.flowNodeType as TVFlowNodeNextTypeEnum);
}

function resolveLeftNodePosition(params: {
  currentPosition: LeftLinkedNodePoint;
  config: LeftLinkedNodeConfig;
  index: number;
  count: number;
  defaultOffset: LeftLinkedNodePoint;
  verticalGap: number;
}) {
  const {
    currentPosition,
    config,
    index,
    count,
    defaultOffset,
    verticalGap,
  } = params;
  const offset = config.offset ?? {
    x: defaultOffset.x,
    y: defaultOffset.y + (index - (count - 1) / 2) * verticalGap,
  };

  return {
    x: currentPosition.x + offset.x,
    y: currentPosition.y + offset.y,
  };
}

function updateCurrentNodeData<TCurrentData extends LinkedNodeData>(
  currentData: TCurrentData,
  leftNodes: WorkflowNodeEntity[],
  currentDataPatch?: CurrentDataPatch<TCurrentData>,
) {
  const patch = typeof currentDataPatch === 'function'
    ? currentDataPatch({ currentData, leftNodes })
    : currentDataPatch;

  const nextData = {
    ...currentData,
    ...(patch ?? {}),
  } as TCurrentData;

  return {
    ...nextData,
    inputNodeId: mergeLeftNodeIdsToInputNodeId(nextData.inputNodeId, leftNodes),
  } as TCurrentData;
}

/**
 * 左侧补节点也统一使用 TeamFlow 计数器命名，忽略调用方 data.name。
 */
function createLeftNodeData(
  currentNode: WorkflowNodeEntity,
  config: LeftLinkedNodeConfig,
) {
  const nodeName = getNextNodeNameByCurrentType({
    currentNode,
    nextNodeType: config.type,
    fallbackName: NODE_NAME_CONFIG[config.type] ?? 'roboneo_web_node',
  });
  const data = {
    ...config.data,
    name: nodeName,
  };

  if (config.type === TVFlowNodeNextTypeEnum.CONTAINER_NODE) {
    return {
      ...data,
      title: nodeName,
    };
  }

  return data;
}

/**
 * 兼容手动传 leftNodes 与配置驱动 quickStartType 两种调用方式。
 */
function resolveApplyParams<TCurrentData extends LinkedNodeData>(
  params: ApplyLeftLinkedNodesParams<TCurrentData>,
): {
    leftNodes: LeftLinkedNodeConfig[];
    currentDataPatch?: CurrentDataPatch<TCurrentData>;
  } {
  if ('quickStartType' in params) {
    const resolvedQuickStart = resolveQuickStartApplyConfig<TCurrentData>({
      quickStartType: params.quickStartType,
      modelConfig: params.modelConfig,
    });
    return {
      leftNodes: resolvedQuickStart?.leftNodes ?? [],
      currentDataPatch: params.currentDataPatch ?? resolvedQuickStart?.currentDataPatch,
    };
  }

  return {
    leftNodes: params.leftNodes,
    currentDataPatch: params.currentDataPatch,
  };
}

/**
 * 在当前节点左侧批量创建输入节点，并统一建立 left.output -> current.input 连线。
 *
 * 适用场景：
 * - 快速开始类动作需要自动补齐前置输入节点。
 * - 例如文本节点“图片反推提示词”：在文本节点左侧创建图片节点，并连到文本节点 input。
 *
 * 行为说明：
 * - 只支持左侧输入节点连到当前节点：left.output -> current.input。
 * - 会按节点类型把新节点 id 合并进 currentNode.data.inputNodeId，作为 ADD_LINE 事件同步前的兜底。
 * - 不处理删除、替换已有输入、右侧输出节点、真实任务执行。
 *
 * 使用示例：
 * applyLeftLinkedNodes(ctx, {
 *   currentNode: textNode,
 *   leftNodes: [{ type: TVFlowNodeNextTypeEnum.IMAGE_NODE, data: imageNodeData }],
 *   currentDataPatch: { action: 'image_reverse_prompt' },
 * });
 */
export function applyLeftLinkedNodes<TCurrentData extends LinkedNodeData = LinkedNodeData>(
  ctx: FreeLayoutPluginContext,
  params: ApplyLeftLinkedNodesParams<TCurrentData>,
): ApplyLeftLinkedNodesResult<TCurrentData> {
  const {
    currentNode,
    defaultOffset = DEFAULT_LEFT_NODE_OFFSET,
    verticalGap = DEFAULT_LEFT_NODE_VERTICAL_GAP,
  } = params;
  const {
    leftNodes: leftNodeConfigs,
    currentDataPatch,
  } = resolveApplyParams(params);

  const currentPosition = getCurrentNodePosition(ctx, currentNode);
  const leftNodes = leftNodeConfigs.map((config, index) => {
    const position = resolveLeftNodePosition({
      currentPosition,
      config,
      index,
      count: leftNodeConfigs.length,
      defaultOffset,
      verticalGap,
    });

    return ctx.document.createWorkflowNode({
      id: config.id ?? nanoid(),
      type: config.type,
      meta: {
        position,
      },
      data: createLeftNodeData(currentNode, config),
    });
  });

  const currentData = (currentNode.toJSON()?.data ?? {}) as TCurrentData;
  const nextCurrentData = updateCurrentNodeData(
    currentData,
    leftNodes,
    currentDataPatch,
  );

  currentNode.form?.updateFormValues(nextCurrentData);

  leftNodes.forEach((leftNode) => {
    ctx.document.linesManager.createLine({
      from: leftNode.id,
      fromPort: 'output',
      to: currentNode.id,
      toPort: 'input',
    });
  });

  return {
    leftNodes,
    currentData: nextCurrentData,
  };
}
